import { useEffect, useMemo, useRef, useState } from "react";
import "./EditarDesvio.css";

const UP = (v) => (typeof v === "string" ? v.toUpperCase() : v ?? "");
const ATIVO_OPTIONS = [
  { value: 1, label: "SIM" },
  { value: 0, label: "NÃO" },
];

function EditarDesvioDropdown({ label, name, value, options, onSelect, placeholder = "SELECIONE" }) {
  const [open, setOpen] = useState(false);
  const [renderMenu, setRenderMenu] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [menuMaxHeight, setMenuMaxHeight] = useState(220);
  const wrapRef = useRef(null);
  const closeTimerRef = useRef(null);
  const itemHeightRef = 44;

  const current = useMemo(() => options.find((o) => o.value === value) || null, [options, value]);

  useEffect(() => {
    function onDocDown(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) closeMenu();
    }
    document.addEventListener("mousedown", onDocDown);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    const onWindowChange = () => {
      updatePlacement();
    };

    window.addEventListener("resize", onWindowChange);
    window.addEventListener("scroll", onWindowChange, true);
    return () => {
      window.removeEventListener("resize", onWindowChange);
      window.removeEventListener("scroll", onWindowChange, true);
    };
  }, [open, options.length]);

  function updatePlacement() {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const modal = wrapRef.current.closest(".dved-box");
    const modalRect = modal?.getBoundingClientRect();

    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const safePad = 12;

    const viewportBelow = Math.max(0, viewportH - rect.bottom - safePad);
    const viewportAbove = Math.max(0, rect.top - safePad);
    const modalBelow = modalRect ? Math.max(0, modalRect.bottom - rect.bottom - safePad) : Number.POSITIVE_INFINITY;
    const modalAbove = modalRect ? Math.max(0, rect.top - modalRect.top - safePad) : Number.POSITIVE_INFINITY;

    const spaceBelow = Math.max(0, Math.min(viewportBelow, modalBelow));
    const spaceAbove = Math.max(0, Math.min(viewportAbove, modalAbove));

    const target = Math.min(320, options.length * itemHeightRef + 12);
    const shouldOpenUp = spaceBelow < 190 && spaceAbove > spaceBelow;
    const available = shouldOpenUp ? spaceAbove : spaceBelow;
    const dynamicMax = Math.max(96, Math.min(target, available - 6));

    setOpenUp(shouldOpenUp);
    setMenuMaxHeight(dynamicMax);
  }

  const openMenu = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setRenderMenu(true);
    requestAnimationFrame(() => {
      updatePlacement();
      setOpen(true);
    });
  };

  const closeMenu = () => {
    setOpen(false);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setRenderMenu(false), 240);
  };

  const toggle = () => {
    if (open) closeMenu();
    else openMenu();
  };

  return (
    <div ref={wrapRef} className={`dved-field dved-field-select ${open ? "is-open" : ""} ${openUp ? "is-open-up" : ""}`}>
      <label className="dved-label">{label}</label>
      <div className="dved-pick-anchor">
        <button
          type="button"
          className="dved-pick-btn"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={toggle}
        >
          <span className={`dved-pick-val ${current ? "" : "is-placeholder"}`}>
            {current ? current.label : placeholder}
          </span>
        </button>

        {renderMenu && (
          <div
            className={`dved-pick-menu ${open ? "is-open" : "is-closing"}`}
            role="listbox"
            aria-label={label}
            style={{ maxHeight: `${menuMaxHeight}px` }}
          >
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className="dved-pick-opt"
                onClick={() => {
                  onSelect(name, opt.value);
                  closeMenu();
                }}
                role="option"
                aria-selected={opt.value === value}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function EditarDesvio({
  open,
  registro,
  data,
  onClose,
  onSalvar,
  onConfirm,
}) {
  const rec = registro || data || null;
  const submit = onConfirm || onSalvar || (() => {});

  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (!rec) {
      setForm(null);
      return;
    }

    const causadorAtual = rec.causador ?? rec._causador ?? rec.causador_atual ?? "";
    const primariaAtual = rec.causa_primaria ?? rec._primaria ?? rec.causa_primaria_atual ?? "";

    setForm({
      id: rec.id,
      causador_atual: causadorAtual,
      causa_primaria_atual: primariaAtual,
      causador: causadorAtual,
      causa_primaria: primariaAtual,
      causa_secundaria: rec.causa_secundaria ?? "",
      quando_utilizado: rec.quando_utilizado ?? "",
      ativo: Number(rec.ativo) ? 1 : 0,
    });
  }, [open, rec]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open || !form) return null;

  const handleSubmit = () => {
    submit({
      id: form.id,
      causador_atual: form.causador_atual,
      causa_primaria_atual: form.causa_primaria_atual,
      causador: form.causador,
      causa_primaria: form.causa_primaria,
      causa_secundaria: form.causa_secundaria,
      quando_utilizado: form.quando_utilizado,
      ativo: form.ativo,
    });
  };

  return (
    <div className="dved-bg">
      <div className="dved-box" role="dialog" aria-modal="true" aria-label="Editar desvio">
        <header className="dved-head">
          <h3 className="dved-title">Editar desvio</h3>
          <button className="dved-x" type="button" onClick={onClose} aria-label="Fechar">x</button>
        </header>

        <div className="dved-body">
          <div className="dved-row">
            <div className="dved-field">
              <label className="dved-label">Causador</label>
              <input className="dved-input" value={form.causador} disabled readOnly />
            </div>
            <div className="dved-field">
              <label className="dved-label">Causa Primaria</label>
              <input
                className="dved-input"
                value={form.causa_primaria}
                onChange={(e) => setForm((s) => ({ ...s, causa_primaria: UP(e.target.value) }))}
              />
            </div>
          </div>

          <div className="dved-row">
            <div className="dved-field">
              <label className="dved-label">Causa Secundaria</label>
              <input
                className="dved-input"
                value={form.causa_secundaria}
                onChange={(e) => setForm((s) => ({ ...s, causa_secundaria: UP(e.target.value) }))}
              />
            </div>
            <EditarDesvioDropdown
              label="Ativo"
              name="ativo"
              value={form.ativo}
              options={ATIVO_OPTIONS}
              onSelect={(name, nextValue) => setForm((s) => ({ ...s, [name]: Number(nextValue) }))}
            />
          </div>

          <div className="dved-row">
            <div className="dved-field dved-grow">
              <label className="dved-label">Quando Utilizado</label>
              <textarea
                className="dved-input"
                rows={3}
                value={form.quando_utilizado}
                onChange={(e) => setForm((s) => ({ ...s, quando_utilizado: e.target.value }))}
              />
            </div>
          </div>
        </div>

        <footer className="dved-foot">
          <button className="dved-btn" type="button" onClick={onClose}>Cancelar</button>
          <button
            className="dved-btn dved-btn-ok"
            type="button"
            onClick={handleSubmit}
            disabled={!form.causador_atual || !form.causa_primaria || !form.causa_secundaria || !form.quando_utilizado}
          >
            Salvar
          </button>
        </footer>
      </div>
    </div>
  );
}
